import { useState, useEffect, useRef, useMemo } from 'react';
import { getVisaoGeralKpis } from '../services/api';
import { construirFiltrosAPI } from '../utils/filtrosHelper';
import { processarDadosVisaoGeral } from '../utils/dataProcessor';
import { compararFiltrosProfundo, serializarFiltros } from '../utils/compararFiltros';
import { calcularPeriodoComparacao } from '../utils/periodoComparacao';

/**
 * Hook customizado para gerenciar os dados da visão geral
 * @param {Object} filtrosSelecionados - Filtros selecionados pelo usuário
 * @returns {Object} Objeto com dados, loading e função de recarregar
 */
export const useVisaoGeral = (filtrosSelecionados) => {
  const [dados, setDados] = useState({
    faturamento: {
      valorTotal: null,
      percentualCrescimento: null,
      projecao: null,
    },
    lucro: {
      valorTotal: null,
      percentualCrescimento: null,
      projecao: null,
      margem: null,
    },
    custo: {
      valorTotal: null,
      percentualCrescimento: null,
    },
    litros: {
      valorTotal: null,
      percentualCrescimento: null,
      valorAnterior: null,
      volume: {
        produto: null,
        percentual: null,
      },
    },
    abastecimento: {
      valorTotal: null,
      percentualCrescimento: null,
      valorAnterior: null,
      pico: {
        turno: null,
        percentual: null,
      },
    },
    postos: {
      total: null,
      acimaMedia: 0,
    },
    formasPagamento: [],
    abastecimentosTurno: {
      turnos: [],
      turnosMaiores: [],
      percentualTurnosMaiores: 0,
      totalAbastecimentos: 0,
    },
    textoComparacao: 'mês anterior', // Texto padrão para comparação
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Ref para rastrear a requisição atual e evitar race conditions
  const requestIdRef = useRef(0);
  const filtrosAnterioresRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Memoizar os filtros para evitar comparações desnecessárias
  const filtrosMemoizados = useMemo(() => {
    return filtrosSelecionados || {};
  }, [serializarFiltros(filtrosSelecionados)]);

  // cálculo do período de comparação foi extraído para utils/periodoComparacao

  const carregarDados = async (filtrosParaUsar) => {
    // Incrementar ID da requisição para rastrear a mais recente
    const currentRequestId = ++requestIdRef.current;

    try {
      setLoading(true);
      setError(null);
      
      // Preparar filtros para período atual
      const filtrosAPI = construirFiltrosAPI(filtrosParaUsar);
      
      // Preparar filtros para período de comparação
      let filtrosAPIComparacao = null;
      let textoComparacao = 'mês anterior'; // Texto padrão
      
      if (filtrosParaUsar?.dataMin && filtrosParaUsar?.dataMax) {
        const periodoComparacao = calcularPeriodoComparacao(
          filtrosParaUsar.dataMin,
          filtrosParaUsar.dataMax,
          filtrosParaUsar.periodo || 'customizado'
        );
        
        if (periodoComparacao) {
          const filtrosComparacao = {
            ...filtrosParaUsar,
            periodo: 'customizado',
            dataMin: periodoComparacao.dataMin,
            dataMax: periodoComparacao.dataMax,
          };
          filtrosAPIComparacao = construirFiltrosAPI(filtrosComparacao);
          textoComparacao = periodoComparacao.textoComparacao;
        }
      } else {
        // Se não há datas definidas, usar período padrão (hoje vs ontem)
        const hoje = new Date();
        
        const formatarData = (data) => {
          const ano = data.getFullYear();
          const mes = String(data.getMonth() + 1).padStart(2, '0');
          const dia = String(data.getDate()).padStart(2, '0');
          return `${ano}-${mes}-${dia}`;
        };
        
        const ontem = new Date(hoje);
        ontem.setDate(ontem.getDate() - 1);
        
        const filtrosComparacao = {
          ...filtrosParaUsar,
          periodo: 'customizado',
          dataMin: formatarData(ontem),
          dataMax: formatarData(ontem),
        };
        filtrosAPIComparacao = construirFiltrosAPI(filtrosComparacao);
        textoComparacao = 'ontem';
      }
      
      // OTIMIZAÇÃO: Buscar período atual e período de comparação em PARALELO
      const [resposta, respostaComparacao] = await Promise.all([
        getVisaoGeralKpis(filtrosAPI),
        filtrosAPIComparacao ? getVisaoGeralKpis(filtrosAPIComparacao) : Promise.resolve(null)
      ]);
      
      // Verificar se ainda é a requisição mais recente antes de atualizar o estado
      if (currentRequestId !== requestIdRef.current) {
        return; // Uma requisição mais recente já foi iniciada
      }
      
      const dadosProcessados = processarDadosVisaoGeral(resposta);
      
      // Adicionar texto de comparação aos dados processados
      dadosProcessados.textoComparacao = textoComparacao;
      
      // Calcular porcentagem de crescimento comparando com o período anterior
      if (respostaComparacao) {
        const dadosComparacao = processarDadosVisaoGeral(respostaComparacao);
        
        // Calcular crescimento do Faturamento
        if (dadosProcessados.faturamento.valorTotal !== null && dadosComparacao.faturamento.valorTotal !== null) {
          const valorAtual = parseFloat(dadosProcessados.faturamento.valorTotal) || 0;
          const valorAnterior = parseFloat(dadosComparacao.faturamento.valorTotal) || 0;
          
          let percentualCrescimento = null;
          if (valorAnterior > 0) {
            percentualCrescimento = ((valorAtual - valorAnterior) / valorAnterior) * 100;
          } else if (valorAtual > 0) {
            percentualCrescimento = 100;
          }

          dadosProcessados.faturamento.percentualCrescimento = percentualCrescimento;
          dadosProcessados.faturamento.valorAnterior = valorAnterior;
        }
        
        // Calcular crescimento do Lucro
        if (dadosProcessados.lucro.valorTotal !== null && dadosComparacao.lucro.valorTotal !== null) {
          const valorAtual = parseFloat(dadosProcessados.lucro.valorTotal) || 0;
          const valorAnterior = parseFloat(dadosComparacao.lucro.valorTotal) || 0;
          
          let percentualCrescimento = null;
          if (valorAnterior > 0) {
            percentualCrescimento = ((valorAtual - valorAnterior) / valorAnterior) * 100;
          } else if (valorAtual > 0) {
            percentualCrescimento = 100;
          } else if (valorAtual < 0 && valorAnterior < 0) {
            // Ambos negativos: calcular crescimento relativo
            percentualCrescimento = ((valorAtual - valorAnterior) / Math.abs(valorAnterior)) * 100;
          }
          
          dadosProcessados.lucro.percentualCrescimento = percentualCrescimento;
          dadosProcessados.lucro.valorAnterior = valorAnterior;
        }
        
        // Calcular crescimento do Custo
        if (dadosProcessados.custo.valorTotal !== null && dadosComparacao.custo.valorTotal !== null) {
          const valorAtual = parseFloat(dadosProcessados.custo.valorTotal) || 0;
          const valorAnterior = parseFloat(dadosComparacao.custo.valorTotal) || 0;
          
          let percentualCrescimento = null;
          if (valorAnterior > 0) {
            percentualCrescimento = ((valorAtual - valorAnterior) / valorAnterior) * 100;
          } else if (valorAtual > 0) {
            percentualCrescimento = 100;
          }
          
          dadosProcessados.custo.percentualCrescimento = percentualCrescimento;
          dadosProcessados.custo.valorAnterior = valorAnterior;
        }
        
        // Calcular crescimento do Litros
        if (dadosProcessados.litros.valorTotal !== null && dadosComparacao.litros.valorTotal !== null) {
          const valorAtual = parseFloat(dadosProcessados.litros.valorTotal) || 0;
          const valorAnterior = parseFloat(dadosComparacao.litros.valorTotal) || 0;
          
          let percentualCrescimento = null;
          if (valorAnterior > 0) {
            percentualCrescimento = ((valorAtual - valorAnterior) / valorAnterior) * 100;
          } else if (valorAtual > 0) {
            percentualCrescimento = 100;
          }
          
          dadosProcessados.litros.percentualCrescimento = percentualCrescimento;
          dadosProcessados.litros.valorAnterior = valorAnterior;
        }
        
        // Calcular crescimento do Abastecimento
        if (dadosProcessados.abastecimento.valorTotal !== null && dadosComparacao.abastecimento.valorTotal !== null) {
          const valorAtual = parseFloat(dadosProcessados.abastecimento.valorTotal) || 0;
          const valorAnterior = parseFloat(dadosComparacao.abastecimento.valorTotal) || 0;
          
          let percentualCrescimento = null;
          if (valorAnterior > 0) {
            percentualCrescimento = ((valorAtual - valorAnterior) / valorAnterior) * 100;
          } else if (valorAtual > 0) {
            percentualCrescimento = 100;
          }
          
          dadosProcessados.abastecimento.percentualCrescimento = percentualCrescimento;
          dadosProcessados.abastecimento.valorAnterior = valorAnterior;
        }
      } else {
        // Se não conseguimos calcular o período de comparação, manter os percentuais como null
      }
      
      // Verificar novamente antes de atualizar (pode ter sido substituída durante o processamento)
      if (currentRequestId === requestIdRef.current) {
        setDados(dadosProcessados);
      }
    } catch (err) {
      // Verificar se ainda é a requisição atual antes de atualizar o erro
      if (currentRequestId === requestIdRef.current) {
        console.error('Erro ao carregar dados da visão geral:', err);
        setError(err);
        setDados({
          faturamento: {
            valorTotal: null,
            percentualCrescimento: null,
            projecao: null,
            valorAnterior: null,
          },
          lucro: {
            valorTotal: null,
            percentualCrescimento: null,
            projecao: null,
            margem: null,
            valorAnterior: null,
          },
          custo: {
            valorTotal: null,
            percentualCrescimento: null,
          },
          litros: {
            valorTotal: null,
            percentualCrescimento: null,
            valorAnterior: null,
            volume: {
              produto: null,
              percentual: null,
            },
          },
          abastecimento: {
            valorTotal: null,
            percentualCrescimento: null,
            valorAnterior: null,
            pico: {
              turno: null,
              percentual: null,
            },
          },
          postos: {
            total: null,
            acimaMedia: 0,
          },
          formasPagamento: [],
          abastecimentosTurno: {
            turnos: [],
            turnosMaiores: [],
            percentualTurnosMaiores: 0,
            totalAbastecimentos: 0,
          },
          textoComparacao: 'período anterior',
        });
      }
    } finally {
      // Só atualizar loading se ainda for a requisição atual
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Comparar filtros para evitar requisições desnecessárias
    if (compararFiltrosProfundo(filtrosMemoizados, filtrosAnterioresRef.current)) {
      return;
    }
    
    // Limpar timer anterior se existir
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce de 1200ms para dar tempo do prefetch completar (batch de 33 requests leva ~12s)
    // O prefetch roda imediatamente e inclui KPIs, então o hook só deve rodar se o prefetch falhar
    debounceTimerRef.current = setTimeout(() => {
      filtrosAnterioresRef.current = filtrosMemoizados;
      carregarDados(filtrosMemoizados);
    }, 1200);
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filtrosMemoizados]);

  return {
    dados,
    loading,
    error,
    recarregar: () => carregarDados(filtrosMemoizados),
  };
};

